import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import AddGroupModal from "./AddGroupModal";
import { IconPlus, IconUsers } from "@tabler/icons-react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import "./OrganizationContent.css";

function AdminTabCustomGroupsPage() {
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState("all");
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [repTypes, setRepTypes] = useState([]);
  const [loadingRepTypes, setLoadingRepTypes] = useState(false);

  const canManageGroups =
    user && (user.role === "MANAGER" || user.role === "COMPANY_ADMIN");

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint(`/v1/groups/owned`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch groups data");
      }

      const result = await response.json();
      setGroups(result.data || []);
    } catch (err) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepTypes = async () => {
    try {
      setLoadingRepTypes(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint(`/v1/company/rep-types`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRepTypes(result.data || []);
      } else {
        setRepTypes([]);
      }
    } catch (error) {
      setRepTypes([]);
    } finally {
      setLoadingRepTypes(false);
    }
  };

  useEffect(() => {
    if (!user || (user.role !== "MANAGER" && user.role !== "COMPANY_ADMIN")) {
      return;
    }

    fetchGroups();
    fetchRepTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const handleGroupClick = groupId => {
    navigate(`/group/${groupId}`);
  };

  const handleGroupAdded = () => {
    setAddGroupModalOpen(false);
    fetchGroups();
  };

  const filteredGroups = groups.filter(group => {
    if (groupFilter === "all") return true;
    if (groupFilter === "auto_add") return group.auto_add;
    if (groupFilter === "chat") return group.enable_chat;
    if (groupFilter === "downline") return group.include_downline;
    return true;
  });

  const autoAddCount = groups.filter(g => g.auto_add).length;
  const chatCount = groups.filter(g => g.enable_chat).length;
  const downlineCount = groups.filter(g => g.include_downline).length;

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="lg" />
        <Text>Loading custom groups...</Text>
      </Group>
    );
  }

  return (
    <Stack gap="xl">
      <div className="org-section-content">
        <div className="carousel-section">
          <Group justify="space-between" mb="md">
            <div>
              <Title order={3}>Custom Groups</Title>
              <Text c="dimmed" size="sm">
                {groups.length} {groups.length === 1 ? "group" : "groups"}
              </Text>
            </div>
            {canManageGroups && (
              <Button
                leftSection={<IconPlus size={14} />}
                onClick={() => setAddGroupModalOpen(true)}
                variant="default"
                size="md"
                radius="md"
                style={{
                  backgroundColor: "white",
                  border: "1px solid #dee2e6",
                  color: "#2d3748",
                  fontWeight: 500,
                }}
              >
                New Group
              </Button>
            )}
          </Group>

          <Group gap="xs" mb="xl">
            {[
              { key: "all", label: "All" },
              { key: "auto_add", label: `Auto Add (${autoAddCount})` },
              { key: "chat", label: `Chat Enabled (${chatCount})` },
              { key: "downline", label: `Downline (${downlineCount})` },
            ].map(({ key, label }) => (
              <Button
                key={key}
                size="sm"
                variant="default"
                radius="md"
                onClick={() => setGroupFilter(key)}
                style={{
                  backgroundColor: groupFilter === key ? "#f1f3f5" : "white",
                  border: "1px solid #dee2e6",
                  color: "#2d3748",
                  fontWeight: groupFilter === key ? 600 : 400,
                }}
              >
                {label}
              </Button>
            ))}
          </Group>

          {groups.length === 0 ? (
            <Stack align="center" gap="md" py="lg">
              <IconUsers size={48} color="var(--mantine-color-gray-5)" />
              <Text ta="center" c="dimmed" size="lg">
                No groups available at this time.
              </Text>
              {canManageGroups && (
                <Button
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setAddGroupModalOpen(true)}
                  variant="light"
                >
                  Create Your First Group
                </Button>
              )}
            </Stack>
          ) : filteredGroups.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="xl" fs="italic">
              No groups in this category
            </Text>
          ) : (
            <div className="course-cards-grid">
              {filteredGroups.map(group => (
                <div
                  key={group.id}
                  className="course-card"
                  onClick={() => handleGroupClick(group.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleGroupClick(group.id);
                    }
                  }}
                >
                  <div className="course-card__thumbnail">
                    <div
                      className="course-card__placeholder"
                      style={{
                        background:
                          "linear-gradient(135deg, #4dabf7 0%, #228be6 100%)",
                      }}
                    >
                      <IconUsers size={44} color="rgba(255,255,255,0.85)" />
                    </div>
                    <div className="course-card__gradient-overlay" />
                  </div>

                  <div className="course-card__content">
                    <h3 className="course-card__title">
                      {group.name || "Unnamed Group"}
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                        marginTop: 4,
                      }}
                    >
                      {group.enable_chat && (
                        <Badge color="green" variant="light" size="xs">
                          Chat
                        </Badge>
                      )}
                      {group.auto_add && (
                        <Badge color="blue" variant="light" size="xs">
                          Auto Add
                        </Badge>
                      )}
                      {group.include_downline && (
                        <Badge color="grape" variant="light" size="xs">
                          Downline
                        </Badge>
                      )}
                      {group.rep_type && (
                        <Badge color="gray" variant="light" size="xs">
                          {group.rep_type.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AddGroupModal
        isOpen={addGroupModalOpen}
        onClose={() => setAddGroupModalOpen(false)}
        onGroupAdded={handleGroupAdded}
        repTypes={repTypes}
        loadingRepTypes={loadingRepTypes}
      />
    </Stack>
  );
}

export default AdminTabCustomGroupsPage;
